import BugReportsAPI from '../../api/bugReports';
import BugReportCommentsAPI from '../../api/bugReportComments';

const types = {
  SET_UI_FLAGS: 'SET_UI_FLAGS',
  SET_BUG_REPORTS: 'SET_BUG_REPORTS',
  SET_BUG_REPORT: 'SET_BUG_REPORT',
  SET_META: 'SET_META',
  SET_COMMENTS: 'SET_COMMENTS',
  ADD_COMMENT: 'ADD_COMMENT',
  APPEND_COMMENTS: 'APPEND_COMMENTS',
  SET_COMMENTS_META: 'SET_COMMENTS_META',
  SET_TIMELINE: 'SET_TIMELINE',
  APPEND_TIMELINE_ITEM: 'APPEND_TIMELINE_ITEM',
};

const state = {
  records: {},
  sortOrder: [],
  comments: {},
  commentsMeta: {},
  timeline: {},
  meta: {
    totalCount: 0,
    currentPage: 1,
  },
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isFetchingComments: false,
    isCreatingComment: false,
    hasFetchCommentsError: false,
    hasFetchItemError: false,
    isFetchingTimeline: false,
    hasFetchTimelineError: false,
  },
};

const getters = {
  getAll($state) {
    return $state.sortOrder.map(id => $state.records[id]).filter(Boolean);
  },
  getById: $state => id => $state.records[id],
  getComments: $state => bugReportId => $state.comments[bugReportId] || [],
  getCommentsMeta: $state => bugReportId => $state.commentsMeta[bugReportId] || { totalCount: 0 },
  getTimeline: $state => bugReportId => $state.timeline[bugReportId] || [],
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getMeta($state) {
    return $state.meta;
  },
};

const actions = {
  fetchAll: async ({ commit }, { page = 1, status, search, userId, dateFrom, dateTo, perPage = 25, labelIds } = {}) => {
    commit(types.SET_UI_FLAGS, { isFetching: true });
    try {
      const { data } = await BugReportsAPI.get({ page, status, search, userId, dateFrom, dateTo, perPage, labelIds });
      const records = data.bug_reports || [];
      commit(types.SET_BUG_REPORTS, records);
      commit(types.SET_META, {
        totalCount: data.meta?.count ?? records.length,
        currentPage: page,
      });
    } finally {
      commit(types.SET_UI_FLAGS, { isFetching: false });
    }
  },

  fetchOne: async ({ commit }, id) => {
    commit(types.SET_UI_FLAGS, { isFetchingItem: true, hasFetchItemError: false });
    try {
      const { data } = await BugReportsAPI.show(id);
      commit(types.SET_BUG_REPORT, data);
    } catch {
      commit(types.SET_UI_FLAGS, { hasFetchItemError: true });
      throw new Error('fetch_item_failed');
    } finally {
      commit(types.SET_UI_FLAGS, { isFetchingItem: false });
    }
  },

  create: async ({ commit }, bugReportData) => {
    commit(types.SET_UI_FLAGS, { isCreating: true });
    try {
      const { data } = await BugReportsAPI.create(bugReportData);
      return data;
    } finally {
      commit(types.SET_UI_FLAGS, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, status }) => {
    commit(types.SET_UI_FLAGS, { isUpdating: true });
    try {
      const { data } = await BugReportsAPI.update(id, status);
      if (data.success) {
        commit(types.SET_BUG_REPORT, { id, status });
      }
      return data;
    } finally {
      commit(types.SET_UI_FLAGS, { isUpdating: false });
    }
  },

  fetchComments: async ({ commit }, { bugReportId, page = 1 }) => {
    commit(types.SET_UI_FLAGS, { isFetchingComments: true, hasFetchCommentsError: false });
    try {
      const { data } = await BugReportCommentsAPI.index(bugReportId, { page });
      const comments = data.comments || [];
      if (page === 1) {
        commit(types.SET_COMMENTS, { bugReportId, comments });
      } else {
        commit(types.APPEND_COMMENTS, { bugReportId, comments });
      }
      commit(types.SET_COMMENTS_META, { bugReportId, totalCount: data.meta?.count ?? 0 });
    } catch {
      commit(types.SET_UI_FLAGS, { hasFetchCommentsError: true });
      throw new Error('fetch_comments_failed');
    } finally {
      commit(types.SET_UI_FLAGS, { isFetchingComments: false });
    }
  },

  createComment: async ({ commit }, { bugReportId, content }) => {
    commit(types.SET_UI_FLAGS, { isCreatingComment: true });
    try {
      const { data } = await BugReportCommentsAPI.create(bugReportId, content);
      commit(types.ADD_COMMENT, { bugReportId, comment: data });
      // Append immediately to timeline so UI updates without a full re-fetch
      commit(types.APPEND_TIMELINE_ITEM, {
        bugReportId,
        item: {
          id: data.id,
          key: `comment_${data.id}`,
          type: 'comment',
          created_at: data.created_at,
          user: data.user,
          data: { content: data.content },
        },
      });
      return data;
    } finally {
      commit(types.SET_UI_FLAGS, { isCreatingComment: false });
    }
  },

  fetchTimeline: async ({ commit }, bugReportId) => {
    commit(types.SET_UI_FLAGS, { isFetchingTimeline: true, hasFetchTimelineError: false });
    try {
      const { data } = await BugReportsAPI.timeline(bugReportId);
      commit(types.SET_TIMELINE, { bugReportId, items: data.timeline || [] });
    } catch {
      commit(types.SET_UI_FLAGS, { hasFetchTimelineError: true });
      throw new Error('fetch_timeline_failed');
    } finally {
      commit(types.SET_UI_FLAGS, { isFetchingTimeline: false });
    }
  },
};

const mutations = {
  [types.SET_UI_FLAGS]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },

  [types.SET_BUG_REPORTS]($state, records) {
    const newRecords = {};
    const newOrder = [];
    records.forEach(record => {
      newRecords[record.id] = record;
      newOrder.push(record.id);
    });
    $state.records = newRecords;
    $state.sortOrder = newOrder;
  },

  [types.SET_BUG_REPORT]($state, record) {
    $state.records = { ...$state.records, [record.id]: { ...$state.records[record.id], ...record } };
  },

  [types.SET_META]($state, meta) {
    $state.meta = { ...$state.meta, ...meta };
  },

  [types.SET_COMMENTS]($state, { bugReportId, comments }) {
    $state.comments = { ...$state.comments, [bugReportId]: comments };
  },

  [types.ADD_COMMENT]($state, { bugReportId, comment }) {
    const existing = $state.comments[bugReportId] || [];
    $state.comments = {
      ...$state.comments,
      [bugReportId]: [...existing, comment],
    };
  },

  [types.APPEND_COMMENTS]($state, { bugReportId, comments }) {
    const existing = $state.comments[bugReportId] || [];
    $state.comments = { ...$state.comments, [bugReportId]: [...existing, ...comments] };
  },

  [types.SET_COMMENTS_META]($state, { bugReportId, totalCount }) {
    $state.commentsMeta = { ...$state.commentsMeta, [bugReportId]: { totalCount } };
  },

  [types.SET_TIMELINE]($state, { bugReportId, items }) {
    $state.timeline = { ...$state.timeline, [bugReportId]: items };
  },

  [types.APPEND_TIMELINE_ITEM]($state, { bugReportId, item }) {
    const existing = $state.timeline[bugReportId] || [];
    // Guard against double-submit: skip if this key is already in the list
    if (existing.some(i => i.key === item.key)) return;
    $state.timeline = { ...$state.timeline, [bugReportId]: [...existing, item] };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
